import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, FormControl,
  InputLabel, Select, MenuItem, Autocomplete, Snackbar, Alert,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Divider, IconButton, CircularProgress, Grid
} from '@mui/material';
import {
  Add, Delete, Search, Receipt, Person, ShoppingCart,
  Payment, Calculate, Send, PersonAdd, Clear, Visibility
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';

// Colores del tema
const THEME = {
  primary: '#005eff',
  accent: '#283593',
  success: '#2e7d32',
  bg: '#f5f6fa',
  cardBg: '#ffffff',
  border: '#e0e0e0',
  textLight: '#757575',
};

export default function CrearFactura() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [secuencialData, setSecuencialData] = useState({ ultimo: 0 });
  const [puntosEmision, setPuntosEmision] = useState([]);
  const [puntoSeleccionado, setPuntoSeleccionado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [openExitoDialog, setOpenExitoDialog] = useState(false);
  const [facturaExitosa, setFacturaExitosa] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [verProductoFactura, setVerProductoFactura] = useState(null);

  const [cliente, setCliente] = useState({
    nombre: '', ruc: '', direccion: '', telefono: '', email: '',
  });

  const [datosFactura, setDatosFactura] = useState({
    fechaEmision: new Date().toISOString().split('T')[0],
    secuencial: ''
  });

  const [detalles, setDetalles] = useState([
    { codigo: '', nombre: '', cantidad: 1, precio: 0, iva: '15%', descuento: 0 },
  ]);

  const [pago, setPago] = useState({
    forma: '01', valor: 0, plazo: '', tiempo: '',
  });
  const [comentarios, setComentarios] = useState('');

  const [totales, setTotales] = useState({
    subtotalSinImpuestos: 0, subtotal15: 0, subtotal0: 0,
    totalDescuento: 0, iva15: 0, valorPagar: 0,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openClienteDialog, setOpenClienteDialog] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(false);
  const [openConfirmFactura, setOpenConfirmFactura] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    identificacion: '', razon_social: '', nombre_comercial: '',
    emails: [], nombre_contacto: '',
    telefono_celular: '', telefono_convencional: '',
    direccion_1: '', direccion_2: '',
    pais: 'ECUADOR', provincia_estado: '', tipo_cliente: 'NACIONAL',
    regimen: 'GENERAL', tipo_persona: 'NATURAL', obligado_contabilidad: 'NO',
    limite_credito: 0, plazo_credito_dias: 0, descuento_porcentaje: 0,
  });
  const [nuevoEmailInput, setNuevoEmailInput] = useState('');
  const [clienteCompleto, setClienteCompleto] = useState(null); // datos completos del cliente seleccionado

  // Cargar datos (aislados por empresa)
  useEffect(() => {
    // Cargar clientes desde API
    const fetchClientes = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/clientes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setClientes(Array.isArray(data) ? data : []);
        } else {
          console.error('Error al cargar clientes:', res.status);
          setClientes([]);
        }
      } catch (error) {
        console.error('Error cargando clientes:', error);
        setClientes([]);
      }
    };
    fetchClientes();
    
    // Cargar productos desde API
    const fetchProductos = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/productos?paraVenta=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Filtrar solo productos creados para venta (excluir los auto-generados por compras/liquidaciones)
          const productosVenta = (Array.isArray(data) ? data : []).filter(p => {
            if (p.activo === false) return false;
            // Excluir productos que fueron creados automáticamente por compras y no tienen precio de venta real
            if (p.origen === 'COMPRA' || p.origen === 'LIQUIDACION') return false;
            // Excluir productos con código AUTO- (generados automáticamente)
            if (p.codigo && p.codigo.startsWith('AUTO-')) return false;
            return true;
          });
          setProductos(productosVenta);
        } else {
          console.error('Error al cargar productos:', res.status);
          setProductos([]);
        }
      } catch (error) {
        console.error('Error cargando productos:', error);
        setProductos([]);
      }
    };
    fetchProductos();

    fetchSecuencial();

    // ── Pre-llenar desde cotización aprobada ──
    const prefill = sessionStorage.getItem("cotizacion_prefill");
    if (prefill) {
      try {
        const datos = JSON.parse(prefill);
        sessionStorage.removeItem("cotizacion_prefill");

        if (datos.cliente) {
          setCliente({
            nombre:    datos.cliente.nombre    || "",
            ruc:       datos.cliente.ruc       || "",
            direccion: datos.cliente.direccion || "",
            telefono:  datos.cliente.telefono  || "",
            email:     datos.cliente.email     || "",
          });
          setClienteSeleccionado(true);
        }

        if (datos.detalles && datos.detalles.length > 0) {
          setDetalles(datos.detalles.map(d => ({
            codigo:    d.codigo    || "",
            nombre:    d.nombre    || "",
            cantidad:  d.cantidad  || 1,
            precio:    d.precio    || 0,
            iva:       d.iva       || "15%",
            descuento: d.descuento || 0,
          })));
        }

        if (datos.cotizacionNumero) {
          setComentarios(`Cotización aprobada: ${datos.cotizacionNumero}`);
        }
      } catch (e) {
        console.warn("Error leyendo prefill de cotización:", e);
      }
    }
  }, []);

  // Obtener secuencial desde el backend (per-empresa)
  const fetchSecuencial = async (estabParam, ptoParam) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_API_URL}/api/secuencial`;
      if (estabParam && ptoParam) {
        url += `?establecimiento=${estabParam}&puntoEmision=${ptoParam}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSecuencialData({ ultimo: data.ultimo });
        const siguiente = data.siguiente;
        const estab = data.establecimiento || '001';
        const ptoEmi = data.ptoEmision || '001';
        setDatosFactura(prev => ({
          ...prev,
          secuencial: `${estab}-${ptoEmi}-${siguiente.toString().padStart(9, '0')}`
        }));
        if (data.puntosEmision && data.puntosEmision.length > 0) {
          setPuntosEmision(data.puntosEmision);
          if (!puntoSeleccionado) {
            setPuntoSeleccionado(data.puntosEmision[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error cargando secuencial:', error);
    }
  };

  // Recalcular totales
  const calcularTotales = useCallback(() => {
    let subtotal15 = 0, subtotal0 = 0, totalDescuento = 0;
    detalles.forEach((item) => {
      const cantidad = parseFloat(item.cantidad) || 0;
      const precio = parseFloat(item.precio) || 0;
      const subtotal = precio * cantidad;
      const descuento = subtotal * (parseFloat(item.descuento || 0) / 100);
      totalDescuento += descuento;
      if (item.iva === '15%') {
        subtotal15 += subtotal - descuento;
      } else {
        subtotal0 += subtotal - descuento;
      }
    });
    const iva15 = subtotal15 * 0.15;
    const valorPagar = subtotal15 + subtotal0 + iva15;
    setTotales({
      subtotalSinImpuestos: parseFloat((subtotal15 + subtotal0).toFixed(2)),
      subtotal15: parseFloat(subtotal15.toFixed(2)),
      subtotal0: parseFloat(subtotal0.toFixed(2)),
      totalDescuento: parseFloat(totalDescuento.toFixed(2)),
      iva15: parseFloat(iva15.toFixed(2)),
      valorPagar: parseFloat(valorPagar.toFixed(2)),
    });
  }, [detalles]);

  useEffect(() => { calcularTotales(); }, [calcularTotales]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Cliente
  const seleccionarCliente = (c) => {
    setCliente({
      nombre: c.razon_social || c.nombre, 
      ruc: c.identificacion || c.ruc, 
      direccion: c.direccion_1 || c.direccion || '',
      telefono: c.telefono_celular || c.telefono || '', 
      email: (c.emails && c.emails[0]) || c.email || c.correo || '',
    });
    setClienteCompleto(c);
    setClienteSeleccionado(true);

    // Consultar crédito disponible real del backend
    if (c.id) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_API_URL}/api/cliente/${c.id}/credito-disponible`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          if (data.limiteCredito !== undefined) {
            setClienteCompleto(prev => ({
              ...prev,
              ...c,
              limite_credito: data.limiteCredito,
              credito_disponible_real: data.creditoDisponible,
              saldo_pendiente_real: data.saldoPendiente,
              plazo_credito_dias: data.plazoDias,
              puede_facturar_credito: data.puedeFacturarCredito,
            }));
          }
        })
        .catch(() => {});
    }
  };

  const limpiarCliente = () => {
    setCliente({ nombre: '', ruc: '', direccion: '', telefono: '', email: '' });
    setClienteCompleto(null);
    setClienteSeleccionado(false);
    setPago({ forma: '01', valor: 0, plazo: '', tiempo: '' });
  };

  // Productos
  const seleccionarProducto = (index, p) => {
    if (!p) return;
    
    const pvp = parseFloat(p.pvp || p.precio_venta || p.precio_1 || 0);
    const nuevos = [...detalles];
    nuevos[index] = {
      ...nuevos[index],
      codigo: p.codigo || '',
      nombre: p.nombre || '',
      precio: pvp.toString(),
      iva: p.iva_porcentaje || (p.iva ? (p.iva.toString().includes('%') ? p.iva : `${p.iva}%`) : '15%'),
      pvp: pvp,
      precio_1: parseFloat(p.precio_1 || 0),
      precio_2: parseFloat(p.precio_2 || 0),
      precio_3: parseFloat(p.precio_3 || 0),
      tipoPrecio: 'pvp',
      imagen_url: p.imagen_url || '',
      stock: parseFloat(p.cantidad_stock || p.stock_actual || p.stock_total || 0),
      tipo: p.tipo || 'PRODUCTO',
      productoCompleto: p,
    };
    setDetalles(nuevos);
  };

  const cambiarTipoPrecio = (index, tipo) => {
    const nuevos = [...detalles];
    const item = nuevos[index];
    const precios = { 'pvp': item.pvp, '1': item.precio_1, '2': item.precio_2, '3': item.precio_3 };
    nuevos[index] = { ...item, tipoPrecio: tipo, precio: (precios[tipo] || 0).toString() };
    setDetalles(nuevos);
  };

  const handleDetalleChange = (index, campo, valor) => {
    const nuevos = [...detalles];
    nuevos[index][campo] = valor;
    setDetalles(nuevos);
  };

  const agregarItem = () => {
    setDetalles([...detalles, { codigo: '', nombre: '', cantidad: 1, precio: 0, iva: '15%', descuento: 0 }]);
  };

  const eliminarItem = (index) => {
    if (detalles.length > 1) {
      const nuevos = [...detalles];
      nuevos.splice(index, 1);
      setDetalles(nuevos);
    }
  };

  // Calcular subtotal por línea
  const getLineTotal = (item) => {
    const cant = parseFloat(item.cantidad) || 0;
    const precio = parseFloat(item.precio) || 0;
    const desc = cant * precio * (parseFloat(item.descuento || 0) / 100);
    return (cant * precio - desc).toFixed(2);
  };

  // Crear cliente
  const guardarNuevoCliente = async () => {
    if (!nuevoCliente.identificacion || !nuevoCliente.razon_social) {
      showSnackbar('RUC/Cédula y Razón Social son obligatorios', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/clientes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(nuevoCliente)
      });
      
      if (res.ok) {
        const result = await res.json();
        const creado = result.cliente;
        setClientes([...clientes, creado]);
        seleccionarCliente(creado);
        showSnackbar('Cliente creado exitosamente', 'success');
        setOpenClienteDialog(false);
        setNuevoCliente({
          identificacion: '', razon_social: '', nombre_comercial: '',
          emails: [], nombre_contacto: '',
          telefono_celular: '', telefono_convencional: '',
          direccion_1: '', direccion_2: '',
          pais: 'ECUADOR', provincia_estado: '', tipo_cliente: 'NACIONAL',
          regimen: 'GENERAL', tipo_persona: 'NATURAL', obligado_contabilidad: 'NO',
          limite_credito: 0, plazo_credito_dias: 0, descuento_porcentaje: 0,
        });
        setNuevoEmailInput('');
      } else {
        const error = await res.json();
        showSnackbar(error.error || 'Error al crear cliente', 'error');
      }
    } catch (error) {
      console.error('Error creando cliente:', error);
      showSnackbar('Error de conexión al crear cliente', 'error');
    }
  };

  // Validar y emitir
  const abrirConfirmFactura = () => {
    const errores = [];
    if (!cliente.nombre.trim() || !cliente.ruc.trim()) errores.push('Seleccione o cree un cliente');
    const validos = detalles.filter(i => i.nombre.trim() && parseFloat(i.cantidad) > 0 && parseFloat(i.precio) > 0);
    if (validos.length === 0) errores.push('Agregue al menos un producto válido');
    
    // Validar crédito si forma de pago es Crédito a plazo
    if (pago.forma === 'credito_plazo' && clienteCompleto) {
      const creditoDisponible = clienteCompleto.credito_disponible_real != null ? clienteCompleto.credito_disponible_real : parseFloat(clienteCompleto.limite_credito || 0);
      if (creditoDisponible <= 0) {
        errores.push('El cliente no tiene crédito disponible (límite agotado por facturas pendientes)');
      } else if (totales.valorPagar > creditoDisponible) {
        errores.push(`El total ($${totales.valorPagar.toFixed(2)}) excede el crédito disponible ($${creditoDisponible.toFixed(2)})`);
      }
      const plazo = parseInt(clienteCompleto.plazo_credito_dias || 0);
      if (plazo <= 0) {
        errores.push('El cliente no tiene plazo de crédito asignado');
      }
    }
    
    if (errores.length > 0) {
      showSnackbar(errores.join('. '), 'error');
      return;
    }
    setOpenConfirmFactura(true);
  };

  const handleGuardarFactura = async () => {
    setEnviando(true);
    calcularTotales();

    const token = localStorage.getItem('token');

    // ── Si el cliente viene de cotización y no está en BD, crearlo ahora ──
    if (!clienteCompleto?.id && cliente.ruc && cliente.nombre) {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/clientes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            identificacion: cliente.ruc,
            razon_social:   cliente.nombre,
            emails:         cliente.email ? [cliente.email] : [],
            telefono_celular: cliente.telefono || '',
            direccion_1:    cliente.direccion || '',
            tipo_persona:   cliente.ruc.length === 13 ? 'JURIDICA' : 'NATURAL',
            tipo_cliente:   'NACIONAL',
            regimen:        'GENERAL',
            obligado_contabilidad: 'NO',
          }),
        });
        if (res.ok) {
          const result = await res.json();
          const creado = result.cliente;
          if (creado) {
            setClientes(prev => [...prev, creado]);
            setClienteCompleto(creado);
          }
        }
      } catch (e) {
        console.warn('No se pudo guardar el cliente nuevo:', e);
      }
    }

    const data = { cliente, detalles, pago, totales, comentarios, puntoEmisionSeleccionado: puntoSeleccionado };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/factura`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        if (result.pdf) {
          window.open(`${process.env.REACT_APP_API_URL}${result.pdf}`, '_blank');
        }
        // Refrescar secuencial desde el backend
        try {
          const secRes = await fetch(`${process.env.REACT_APP_API_URL}/api/secuencial`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (secRes.ok) {
            const secData = await secRes.json();
            setSecuencialData({ ultimo: secData.ultimo });
            const estab = secData.establecimiento || '001';
            const ptoEmi = secData.ptoEmision || '001';
            setDatosFactura(prev => ({
              ...prev,
              secuencial: `${estab}-${ptoEmi}-${(secData.siguiente).toString().padStart(9, '0')}`
            }));
          }
        } catch (e) {
          console.error('Error refrescando secuencial:', e);
        }
        showSnackbar(
          result.estadoSRI === 'AUTORIZADO'
            ? 'Factura autorizada por el SRI'
            : `Factura enviada - Estado: ${result.estadoSRI || 'En procesamiento'}`,
          result.estadoSRI === 'AUTORIZADO' ? 'success' : 'info'
        );
        // Guardar datos para el popup
        setFacturaExitosa({
          claveAcceso: result.claveAcceso,
          secuencial: result.secuencial,
          estadoSRI: result.estadoSRI,
          pdf: result.pdf,
        });
        setOpenExitoDialog(true);
        // Limpiar formulario
        limpiarCliente();
        setDetalles([{ codigo: '', nombre: '', cantidad: 1, precio: 0, iva: '15%', descuento: 0 }]);
        setPago({ forma: '01', valor: 0, plazo: '', tiempo: '' });
        setComentarios('');
      } else {
        showSnackbar(result.error || result.mensaje || 'Error al generar la factura', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error de conexión con el servidor', 'error');
    } finally {
      setEnviando(false);
    }
  };

  // ============================
  // RENDER
  // ============================

  const sectionStyle = {
    bgcolor: THEME.cardBg,
    borderRadius: 3,
    border: `1px solid ${THEME.border}`,
    overflow: 'hidden',
    mb: 3,
  };

  const sectionHeader = (icon, title, color = THEME.primary) => (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 3, py: 1.5,
      bgcolor: color, color: '#fff',
    }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', py: 3, px: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        mb: 3, flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Receipt sx={{ fontSize: 32, color: THEME.primary }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={THEME.primary}>
              Nueva Factura
            </Typography>
            <Typography variant="body2" color={THEME.textLight}>
              Emisión de comprobante electrónico SRI
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {puntosEmision.length > 1 && (
            <select
              value={puntoSeleccionado ? `${puntoSeleccionado.establecimiento}-${puntoSeleccionado.puntoEmision}` : ''}
              onChange={(e) => {
                const [est, pto] = e.target.value.split('-');
                const punto = puntosEmision.find(p => p.establecimiento === est && p.puntoEmision === pto);
                setPuntoSeleccionado(punto);
                fetchSecuencial(est, pto);
              }}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc', fontSize: '0.85rem' }}
            >
              {puntosEmision.map(p => (
                <option key={`${p.establecimiento}-${p.puntoEmision}`} value={`${p.establecimiento}-${p.puntoEmision}`}>
                  {p.establecimiento}-{p.puntoEmision} {p.nombre || ''}
                </option>
              ))}
            </select>
          )}
          <Chip
            label={datosFactura.secuencial || 'Cargando...'}
            color="primary" variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.9rem', height: 36 }}
          />
          <Chip
            label={datosFactura.fechaEmision}
            variant="outlined"
            sx={{ height: 36 }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Columna izquierda: Cliente + Productos */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* CLIENTE */}
          <Paper sx={sectionStyle} elevation={0}>
            {sectionHeader(<Person />, 'Datos del Cliente')}
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Autocomplete
                  key={`cliente-${formKey}`}
                  fullWidth
                  options={clientes}
                  getOptionLabel={(o) => o ? `${o.razon_social || o.nombre} — ${o.identificacion || o.ruc}` : ''}
                  onChange={(_, val) => val && seleccionarCliente(val)}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || option.identificacion || option.ruc}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{option.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.identificacion || option.ruc}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Buscar por nombre o RUC..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                      }}
                    />
                  )}
                />
                <Button variant="outlined" startIcon={<PersonAdd />}
                  onClick={() => setOpenClienteDialog(true)}
                  sx={{ whiteSpace: 'nowrap', minWidth: 140 }}
                >
                  Nuevo
                </Button>
                {clienteSeleccionado && (
                  <IconButton color="error" onClick={limpiarCliente} title="Limpiar cliente">
                    <Clear />
                  </IconButton>
                )}
              </Box>

              {clienteSeleccionado ? (
                <Box sx={{
                  bgcolor: '#f0f4ff', borderRadius: 2, p: 2,
                  border: '1px solid #c5cae9',
                  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5,
                }}>
                  <InfoField label="Nombre / Razón Social" value={cliente.nombre} />
                  <InfoField label="RUC / Cédula" value={cliente.ruc} />
                  <InfoField label="Dirección" value={cliente.direccion} />
                  <InfoField label="Teléfono" value={cliente.telefono} />
                  <InfoField label="Email" value={cliente.email} span={2} />
                </Box>
              ) : (
                <Box sx={{
                  bgcolor: '#fafafa', borderRadius: 2, p: 3,
                  border: '1px dashed #ccc', textAlign: 'center',
                }}>
                  <Typography color="text.secondary" variant="body2">
                    Busque un cliente existente o cree uno nuevo
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* PRODUCTOS */}
          <Paper sx={sectionStyle} elevation={0}>
            {sectionHeader(<ShoppingCart />, 'Detalle de Productos / Servicios')}
            <Box sx={{ p: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, color: THEME.textLight, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                      <TableCell sx={{ minWidth: 300 }}>Producto</TableCell>
                      <TableCell width="60" align="center">Stock</TableCell>
                      <TableCell width="70" align="center">Cant.</TableCell>
                      <TableCell width="90" align="center">Precio</TableCell>
                      <TableCell width="100" align="right">P. Unit.</TableCell>
                      <TableCell width="70" align="center">Desc.%</TableCell>
                      <TableCell width="100" align="right">Subtotal</TableCell>
                      <TableCell width="40" align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles.map((item, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                        <TableCell sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.imagen_url && (
                              <img src={item.imagen_url.startsWith('http') ? item.imagen_url : `${process.env.REACT_APP_API_URL}/${item.imagen_url}`}
                                alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Autocomplete
                                key={`prod-${formKey}-${index}`}
                                size="small"
                                options={productos}
                                getOptionLabel={(o) => o ? `${o.codigo} — ${o.nombre}` : ''}
                                onChange={(_, val) => val && seleccionarProducto(index, val)}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.imagen_url && <img src={option.imagen_url.startsWith('http') ? option.imagen_url : `${process.env.REACT_APP_API_URL}/${option.imagen_url}`} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>{option.nombre}</Typography>
                                      <Typography variant="caption" color="text.secondary">{option.codigo} • ${parseFloat(option.pvp || option.precio_1 || option.precio_venta || 0).toFixed(2)}</Typography>
                                    </Box>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField {...params}
                                    placeholder={item.nombre || 'Buscar producto...'}
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                                  />
                                )}
                              />
                              {item.nombre && !item.imagen_url && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                                  {item.codigo}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {item.tipo === 'SERVICIO' ? <Typography variant="caption" color="text.secondary">—</Typography> :
                            <Typography variant="caption" fontWeight={600}>{item.stock || 0}</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <TextField
                            type="number" size="small"
                            value={item.cantidad}
                            onChange={(e) => handleDetalleChange(index, 'cantidad', e.target.value)}
                            inputProps={{ min: 1, style: { textAlign: 'center' } }}
                            sx={{ width: 60 }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {item.nombre && (
                            <Select size="small" value={item.tipoPrecio || 'pvp'}
                              onChange={(e) => cambiarTipoPrecio(index, e.target.value)}
                              sx={{ fontSize: '0.75rem', height: 30 }}>
                              <MenuItem value="pvp">PVP</MenuItem>
                              <MenuItem value="1">P1</MenuItem>
                              <MenuItem value="2">P2</MenuItem>
                              <MenuItem value="3">P3</MenuItem>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            type="number" size="small"
                            value={item.precio}
                            onChange={(e) => handleDetalleChange(index, 'precio', e.target.value)}
                            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                            sx={{ width: 90 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: '0.8rem' } }}>$</InputAdornment>
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <TextField
                            type="number" size="small"
                            value={item.descuento || 0}
                            onChange={(e) => handleDetalleChange(index, 'descuento', e.target.value)}
                            inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                            sx={{ width: 60 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            ${getLineTotal(item)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {item.productoCompleto && (
                            <IconButton size="small" onClick={() => setVerProductoFactura(item.productoCompleto)}>
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          <IconButton size="small" color="error"
                            onClick={() => eliminarItem(index)}
                            disabled={detalles.length === 1}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button variant="text" startIcon={<Add />} onClick={agregarItem}
                sx={{ mt: 1.5, color: THEME.accent }}
              >
                Agregar línea
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Columna derecha: Pago + Totales + Botón */}
        <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>

          {/* FORMA DE PAGO */}
          <Paper sx={sectionStyle} elevation={0}>
            {sectionHeader(<Payment />, 'Forma de Pago', '#455a64')}
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Pago (SRI)</InputLabel>
                <Select value={pago.forma} label="Forma de Pago (SRI)"
                  onChange={(e) => {
                    const forma = e.target.value;
                    if (forma === 'credito_plazo' && clienteCompleto) {
                      setPago({
                        ...pago,
                        forma,
                        plazo: `${clienteCompleto.plazo_credito_dias || 0} días`,
                      });
                    } else {
                      setPago({ ...pago, forma, plazo: '' });
                    }
                  }}
                >
                  <MenuItem value="01">Sin utilización del sistema financiero</MenuItem>
                  <MenuItem value="15">Compensación de deudas</MenuItem>
                  <MenuItem value="16">Tarjeta de débito</MenuItem>
                  <MenuItem value="17">Dinero electrónico</MenuItem>
                  <MenuItem value="18">Tarjeta prepago</MenuItem>
                  <MenuItem value="19">Tarjeta de crédito</MenuItem>
                  <MenuItem value="20">Otros con utilización del sistema financiero</MenuItem>
                  <MenuItem value="21">Endoso de títulos</MenuItem>
                  <MenuItem value="credito_plazo">Crédito (a plazo)</MenuItem>
                </Select>
              </FormControl>
              {pago.forma === 'credito_plazo' && clienteCompleto && (
                <Box sx={{ bgcolor: '#fff3e0', borderRadius: 1, p: 1.5, border: '1px solid #ffe0b2' }}>
                  <Typography variant="caption" color="text.secondary">Límite de crédito</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${parseFloat(clienteCompleto.limite_credito || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Saldo pendiente</Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    ${parseFloat(clienteCompleto.saldo_pendiente_real || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Crédito disponible</Typography>
                  <Typography variant="body2" fontWeight={600} color={clienteCompleto.puede_facturar_credito ? "success.main" : "error.main"}>
                    ${parseFloat((clienteCompleto.credito_disponible_real != null ? clienteCompleto.credito_disponible_real : clienteCompleto.limite_credito) || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Plazo</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {clienteCompleto.plazo_credito_dias || 0} días
                  </Typography>
                </Box>
              )}
              {pago.forma === 'credito_plazo' && !clienteCompleto && (
                <Alert severity="warning" sx={{ py: 0.5 }}>Seleccione un cliente primero</Alert>
              )}
              <TextField fullWidth size="small" label="Plazo" value={pago.plazo}
                onChange={(e) => setPago({ ...pago, plazo: e.target.value })}
                placeholder="Ej: 30 días"
                disabled={pago.forma === 'credito_plazo'}
              />
              <TextField fullWidth size="small" label="Comentarios / Información Adicional"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                placeholder="Detalle adicional que aparecerá en la factura"
                multiline rows={2}
              />
            </Box>
          </Paper>

          {/* TOTALES */}
          <Paper sx={{ ...sectionStyle, position: 'sticky', top: 20 }} elevation={0}>
            {sectionHeader(<Calculate />, 'Resumen', THEME.success)}
            <Box sx={{ p: 0 }}>
              <TotalRow label="Subtotal sin impuestos" value={totales.subtotalSinImpuestos} />
              <TotalRow label="Subtotal 15%" value={totales.subtotal15} />
              <TotalRow label="Subtotal 0%" value={totales.subtotal0} />
              <TotalRow label="Descuento" value={totales.totalDescuento} negative />
              <TotalRow label="IVA 15%" value={totales.iva15} />
              <Divider />
              <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                px: 3, py: 2, bgcolor: THEME.success, color: '#fff',
              }}>
                <Typography variant="subtitle1" fontWeight={700}>TOTAL A PAGAR</Typography>
                <Typography variant="h6" fontWeight={700}>
                  ${totales.valorPagar.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* BOTÓN EMITIR */}
          <Button
            fullWidth variant="contained" size="large"
            startIcon={enviando ? <CircularProgress size={20} color="inherit" /> : <Send />}
            onClick={abrirConfirmFactura}
            disabled={enviando}
            sx={{
              py: 1.8, borderRadius: 3, fontSize: '1rem', fontWeight: 700,
              bgcolor: THEME.primary,
              '&:hover': { bgcolor: THEME.accent },
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(26,35,126,0.3)',
            }}
          >
            {enviando ? 'Procesando...' : 'Emitir Factura Electrónica'}
          </Button>
        </Box>
      </Box>

      {/* DIALOG: Crear Cliente */}
      <Dialog open={openClienteDialog} onClose={() => setOpenClienteDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: THEME.primary, color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd /> Nuevo Cliente
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="RUC / Cédula *" value={nuevoCliente.identificacion}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, identificacion: e.target.value.replace(/\D/g, '') })}
                inputProps={{ maxLength: 13 }} helperText="10 o 13 dígitos" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" label="Razón Social *" value={nuevoCliente.razon_social}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, razon_social: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Nombre Comercial" value={nuevoCliente.nombre_comercial}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre_comercial: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Nombre de Contacto" value={nuevoCliente.nombre_contacto}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre_contacto: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" label="Agregar email" value={nuevoEmailInput}
                onChange={(e) => setNuevoEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nuevoEmailInput.includes('@')) {
                    e.preventDefault();
                    setNuevoCliente(prev => ({ ...prev, emails: [...(prev.emails || []), nuevoEmailInput] }));
                    setNuevoEmailInput('');
                  }
                }}
                placeholder="correo@ejemplo.com (Enter)" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" size="small" fullWidth sx={{ height: 40 }}
                onClick={() => {
                  if (nuevoEmailInput.includes('@')) {
                    setNuevoCliente(prev => ({ ...prev, emails: [...(prev.emails || []), nuevoEmailInput] }));
                    setNuevoEmailInput('');
                  }
                }}>Agregar</Button>
            </Grid>
            {nuevoCliente.emails?.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {nuevoCliente.emails.map((em, i) => (
                    <Chip key={i} label={em} size="small"
                      onDelete={() => setNuevoCliente(prev => ({ ...prev, emails: prev.emails.filter((_, idx) => idx !== i) }))} />
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Teléfono Celular" value={nuevoCliente.telefono_celular}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono_celular: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Teléfono Convencional" value={nuevoCliente.telefono_convencional}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono_convencional: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Dirección 1" value={nuevoCliente.direccion_1}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, direccion_1: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Dirección 2 (referencia)" value={nuevoCliente.direccion_2}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, direccion_2: e.target.value })} />
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Fiscal" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Persona</InputLabel>
                <Select value={nuevoCliente.tipo_persona} label="Tipo Persona"
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, tipo_persona: e.target.value })}>
                  <MenuItem value="NATURAL">Natural</MenuItem>
                  <MenuItem value="JURIDICA">Jurídica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Régimen</InputLabel>
                <Select value={nuevoCliente.regimen} label="Régimen"
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, regimen: e.target.value })}>
                  <MenuItem value="GENERAL">General</MenuItem>
                  <MenuItem value="RIMPE EMPRENDEDOR">RIMPE Emprendedor</MenuItem>
                  <MenuItem value="RIMPE POPULAR">RIMPE Popular</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Obligado Contabilidad</InputLabel>
                <Select value={nuevoCliente.obligado_contabilidad} label="Obligado Contabilidad"
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, obligado_contabilidad: e.target.value })}>
                  <MenuItem value="SI">Sí</MenuItem>
                  <MenuItem value="NO">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Crédito" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Límite Crédito" type="number"
                value={nuevoCliente.limite_credito}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, limite_credito: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Plazo Crédito (días)" type="number"
                value={nuevoCliente.plazo_credito_dias}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, plazo_credito_dias: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Descuento (%)" type="number"
                value={nuevoCliente.descuento_porcentaje}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, descuento_porcentaje: parseFloat(e.target.value) || 0 })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenClienteDialog(false)}>Cancelar</Button>
          <Button onClick={guardarNuevoCliente} variant="contained">Guardar Cliente</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Confirmar Factura */}
      <Dialog open={openConfirmFactura} onClose={() => setOpenConfirmFactura(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          Confirmar Emisión
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Receipt sx={{ fontSize: 48, color: THEME.primary, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              ¿Emitir factura a nombre de:
            </Typography>
            <Typography variant="h6" fontWeight={700} color={THEME.primary}>
              {cliente.nombre}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {cliente.ruc}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {detalles.filter(d => d.nombre).length} producto(s)
            </Typography>
            <Typography variant="h5" fontWeight={700} color={THEME.success} sx={{ mt: 1 }}>
              ${totales.valorPagar.toFixed(2)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 1 }}>
          <Button onClick={() => setOpenConfirmFactura(false)} variant="outlined" sx={{ minWidth: 120 }}>
            Cancelar
          </Button>
          <Button onClick={() => { setOpenConfirmFactura(false); handleGuardarFactura(); }}
            variant="contained" sx={{ minWidth: 120, bgcolor: THEME.success, '&:hover': { bgcolor: '#1b5e20' } }}>
            Emitir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Factura Exitosa */}
      <Dialog open={openExitoDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Box sx={{ fontSize: 48, mb: 1 }}>✅</Box>
          <Typography variant="h6" fontWeight={700}>
            {facturaExitosa?.estadoSRI === 'AUTORIZADO' ? '¡Factura Autorizada!' : 'Factura Enviada al SRI'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {facturaExitosa?.estadoSRI === 'AUTORIZADO'
              ? 'La factura fue autorizada correctamente por el SRI.'
              : 'La factura está siendo procesada por el SRI. El PDF se generará cuando sea autorizada.'}
          </Typography>
          {facturaExitosa?.secuencial && (
            <Chip label={`N° ${facturaExitosa.secuencial}`} color="primary" sx={{ mt: 1 }} />
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3, pt: 1 }}>
          {facturaExitosa?.estadoSRI === 'AUTORIZADO' && facturaExitosa?.claveAcceso && (
            <Button fullWidth variant="contained" color="primary"
              onClick={() => {
                window.open(`${process.env.REACT_APP_API_URL}/facturas_pdf/factura_${facturaExitosa.claveAcceso}.pdf`, '_blank');
              }}>
              📄 Ver PDF
            </Button>
          )}
          <Button fullWidth variant="outlined" color="success"
            onClick={() => {
              setOpenExitoDialog(false);
              // Forzar limpieza completa
              limpiarCliente();
              setDetalles([{ codigo: '', nombre: '', cantidad: 1, precio: 0, iva: '15%', descuento: 0 }]);
              setPago({ forma: '01', valor: 0, plazo: '', tiempo: '' });
              setComentarios('');
              setFacturaExitosa(null);
              setFormKey(prev => prev + 1); // Forzar re-render de Autocompletes
            }}>
            🧾 Hacer otra factura
          </Button>
          <Button fullWidth variant="text" color="inherit"
            onClick={() => { setOpenExitoDialog(false); navigate('/facturaslist'); }}>
            Ir a listado de facturas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ver Producto */}
      <Dialog open={!!verProductoFactura} onClose={() => setVerProductoFactura(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {verProductoFactura && (<>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>👁️ {verProductoFactura.nombre}</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            {verProductoFactura.imagen_url && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img src={verProductoFactura.imagen_url.startsWith('http') ? verProductoFactura.imagen_url : `${process.env.REACT_APP_API_URL}/${verProductoFactura.imagen_url}`}
                  alt={verProductoFactura.nombre} style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain', borderRadius: 8 }} />
              </Box>
            )}
            <Typography variant="body2"><strong>Código:</strong> {verProductoFactura.codigo || "—"}</Typography>
            <Typography variant="body2"><strong>Nombre:</strong> {verProductoFactura.nombre}</Typography>
            <Typography variant="body2"><strong>PVP:</strong> ${parseFloat(verProductoFactura.pvp || verProductoFactura.precio_venta || verProductoFactura.precio_1 || 0).toFixed(2)}</Typography>
            <Typography variant="body2"><strong>Precio 1:</strong> ${parseFloat(verProductoFactura.precio_1 || 0).toFixed(2)}</Typography>
            <Typography variant="body2"><strong>Precio 2:</strong> ${parseFloat(verProductoFactura.precio_2 || 0).toFixed(2)}</Typography>
            <Typography variant="body2"><strong>Precio 3:</strong> ${parseFloat(verProductoFactura.precio_3 || 0).toFixed(2)}</Typography>
            <Typography variant="body2"><strong>Stock:</strong> {parseFloat(verProductoFactura.cantidad_stock || verProductoFactura.stock_actual || 0)}</Typography>
            <Typography variant="body2"><strong>IVA:</strong> {(verProductoFactura.iva_porcentaje || "15").replace("%","")}%</Typography>
            {verProductoFactura.ficha_tecnica && <Typography variant="body2" sx={{ mt: 1 }}><strong>Ficha:</strong> {verProductoFactura.ficha_tecnica}</Typography>}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setVerProductoFactura(null)} variant="contained">Cerrar</Button>
          </DialogActions>
        </>)}
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Componentes auxiliares
function InfoField({ label, value, span }) {
  return (
    <Box sx={{ gridColumn: span === 2 ? '1 / -1' : undefined }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function TotalRow({ label, value, negative }) {
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between',
      px: 3, py: 1.2, '&:hover': { bgcolor: '#f5f5f5' },
    }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500} color={negative ? 'error.main' : 'text.primary'}>
        {negative && value > 0 ? '-' : ''}${value.toFixed(2)}
      </Typography>
    </Box>
  );
}